import React, { useState } from "react";
import { Box, Typography, Button, useMediaQuery } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import initialBlogs from "../../layout/blogCards/BlogsData";
import BlogCards from "../BlogsSections/BlogCards";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useNavigate } from "react-router-dom";

const BlogsHomeSection = () => {
  const isMobile = useMediaQuery("(max-width:700px)");
  const isTablet = useMediaQuery("(max-width:1100px)");
  const navigate = useNavigate();

  const blogs = initialBlogs.slice(0, 3);
  const [isReadMoreHovered, setIsReadMoreHovered] = useState(false);

  // Button position responsive
  const buttonPositionStyles = isTablet
    ? {
        position: "static",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        mt: 2,
        mb: 1,
        zIndex: 3,
        pointerEvents: "auto",
      }
    : {
        position: "absolute",
        right: { xs: 12, sm: 24, md: 32 },
        bottom: { xs: 12, sm: 20, md: 28 },
        marginTop: 0,
        display: "flex",
        alignItems: "center",
        zIndex: 3,
        width: "auto",
        pointerEvents: "auto",
      };

  return (
    <section
      className="max-w-screen-xl"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        margin: "0 auto",
        position: "relative",
      }}
    >
      <Box
        sx={{
          borderRadius: "10px",
          px: { xs: 0, sm: 2.5, md: 6 },
          py: { xs: 0, sm: 4, md: 5 },
          width: "100%",
          margin: { xs: "10px 0", sm: "30px auto", md: "40px auto" },
          boxShadow: "none",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          maxWidth: { xs: "100%", sm: "98%", md: "100%" },
        }}
      >
        <Typography
          variant="h4"
          className="main-heading-font-size"
          sx={{
            fontWeight: 500,
            // fontSize: { xs: "1.10rem", sm: "1.65rem", md: "2.25rem" },
            mb: 1,
            fontFamily: '"Poppins", sans-serif',
            color: "#222",
            lineHeight: 1.2,
            textAlign: "center",
            px: { xs: 0.5, sm: 0 },
            wordBreak: "break-word",
          }}
        >
          Articles & Blog Posts & Useful Information
        </Typography>
        <Typography
          sx={{
            color: "#888",
            fontSize: { xs: "0.94rem", sm: "1rem", md: "15px" },
            lineHeight: 1.6,
            fontFamily: '"Gilroy", sans-serif',
            textAlign: "center",
            maxWidth: { xs: "96vw", sm: 600 },
            px: { xs: 1, sm: 0 },
            mb: 2,
            mx: "auto",
          }}
        >
          Discover the latest insights, tips, and updates about insurance,
          finance, and protecting your future. Stay informed with our expert
          articles and helpful blog posts designed to guide you in making the
          best decisions for your needs.
        </Typography>
        <Box
          sx={{
            width: "100%",
            mt: 1,
            position: "relative",
            display: isMobile ? "flex" : "block",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            // For mobile, stack in column
            px: { xs: 0, sm: 0 },
            pb: { xs: 1.5, sm: 0 },
          }}
        >
          {isMobile ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
                gap: 2,
              }}
            >
              {blogs.map((blog) => (
                <Box
                  key={blog.id}
                  sx={{
                    width: "100%",
                    maxWidth: 400,
                    mx: "auto",
                    px: { xs: 1, sm: 0 },
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <BlogCards
                    blogId={blog.id}
                    blogImage={blog.blogImage}
                    blogHeading={blog.blogHeading}
                    blogDescription={blog.blogDescription}
                    blogCreatedDate={blog.blogCreatedDate}
                    sx={{
                      width: "100%",
                      maxWidth: 400,
                    }}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            // Tablet and desktop: show cards in a row (was previously a slider, but per prompt, slider only for non-mobile)
            <Box
              sx={{
                display: "flex",
                flexDirection: isTablet ? "row" : "row",
                gap: 2,
                justifyContent: "center",
                alignItems: "stretch",
                width: "100%",
                flexWrap: isTablet ? "wrap" : "nowrap",
              }}
            >
              {blogs.map((blog) => (
                <Box
                  key={blog.id}
                  sx={{
                    minWidth: 0,
                    width: isTablet
                      ? "calc(50% - 16px)"
                      : "calc(33.33% - 16px)",
                    maxWidth: 400,
                    display: "flex",
                    alignItems: "stretch",
                    justifyContent: "center",
                  }}
                >
                  <BlogCards
                    blogId={blog.id}
                    blogImage={blog.blogImage}
                    blogHeading={blog.blogHeading}
                    blogDescription={blog.blogDescription}
                    blogCreatedDate={blog.blogCreatedDate}
                    sx={{
                      width: "100%",
                      maxWidth: 400,
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
        {/* Button for mobile/tablet below cards */}
        {isTablet && (
          <Box sx={buttonPositionStyles}>
            <Button
              variant="text"
              sx={{
                color: "#1a6bb8",
                fontWeight: 600,
                fontFamily: '"Poppins", sans-serif',
                borderRadius: "8px",
                px: 1.5,
                py: 0.5,
                fontSize: { xs: "1rem", sm: "1.05rem" },
                textTransform: "none",
                minWidth: 0,
                boxShadow: "none",
                background: "transparent",
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  background: "rgba(255, 197, 132, 0.4)",
                  color: "#1a6bb8",
                },
              }}
              fullWidth={isMobile}
              onClick={() => navigate("/blogs")}
              onMouseEnter={() => setIsReadMoreHovered(true)}
              onMouseLeave={() => setIsReadMoreHovered(false)}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  position: "relative",
                  width: isMobile ? "100%" : "auto",
                  justifyContent: isMobile ? "center" : "flex-start",
                }}
              >
                Read More
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    marginLeft: 6,
                    transition: "opacity 0.25s, transform 0.25s",
                    opacity: isReadMoreHovered ? 1 : 0,
                    transform: isReadMoreHovered
                      ? "translateX(4px)"
                      : "translateX(-8px)",
                    pointerEvents: "none",
                    position: "relative",
                  }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 22 }} />
                </span>
              </span>
            </Button>
          </Box>
        )}
      </Box>
      {/* For desktop/laptop, button floats absolutely in corner */}
      {!isTablet && (
        <Box sx={buttonPositionStyles}>
          <Button
            variant="text"
            sx={{
              color: "#1a6bb8",
              fontWeight: 600,
              fontFamily: '"Poppins", sans-serif',
              borderRadius: "8px",
              px: 1.5,
              py: 0.5,
              fontSize: "1rem",
              textTransform: "none",
              minWidth: 0,
              boxShadow: "none",
              background: "transparent",
              display: "flex",
              alignItems: "center",
              "&:hover": {
                background: "rgba(255, 197, 132, 0.4)",
                color: "#1a6bb8",
              },
            }}
            onClick={() => navigate("/blogs")}
            onMouseEnter={() => setIsReadMoreHovered(true)}
            onMouseLeave={() => setIsReadMoreHovered(false)}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                position: "relative",
              }}
            >
              Read More
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  marginLeft: 6,
                  transition: "opacity 0.25s, transform 0.25s",
                  opacity: isReadMoreHovered ? 1 : 0,
                  transform: isReadMoreHovered
                    ? "translateX(4px)"
                    : "translateX(-8px)",
                  pointerEvents: "none",
                  position: "relative",
                }}
              >
                <ArrowForwardIcon sx={{ fontSize: 22 }} />
              </span>
            </span>
          </Button>
        </Box>
      )}
    </section>
  );
};

export default BlogsHomeSection;
